import { Commit, Dispatch } from "vuex"
import { errorHandler } from "@/composables/errorHandler"
import { database } from "@/firebase"
import { ref, update, serverTimestamp } from "firebase/database"
import {
	getAuth,
	signInAnonymously,
	signInWithEmailAndPassword,
	signInWithEmailLink,
	signOut,
	onAuthStateChanged,
    User,
    UserCredential,
} from "firebase/auth"
import { IClaims, IError, IUserAuth, IAuthGetters, IUserLike } from "@/interfaces"

const { onError } = errorHandler()

/**
 * A shape representing a user-like object that may come from Firebase or the DB.
 * We reuse the shared `IUserLike` from `@/interfaces` but keep a local alias
 * for readability in this module.
 *
 * Fields:
 * - partial IUserAuth fields
 * - optional Firebase flags such as `emailVerified` and `isAnonymous`
 * - optional `color` and `metadata.lastLoginAt`
 */
type UserLike = IUserLike

/**
 * Normalizes a Firebase user or partial user record into the app's
 * IUserAuth shape. This ensures downstream consumers can rely on a
 * consistent set of fields.
 *
 * @param user - Partial or DB user object (may be null/undefined)
 * @returns normalized IUserAuth with safe defaults
 */
export function mapToIUserAuth(user?: UserLike | null): IUserAuth {
	const uid = (user && user.uid) || ""
	const email = (user ? (user.email ?? null) : null) as string | null
	const displayName = (user && (user.displayName ?? "")) as string
	const isAnonymous = !!(user && user.isAnonymous)
	const emailVerified = !!(user && user.emailVerified)
	const metadata = (user && (user.metadata as { lastLoginAt: string })) || { lastLoginAt: "" }
	const color = (user && (user.color ?? "")) as string
	const role = (user && user.role) as string | undefined

	return {
		uid,
		email,
		displayName,
		isAnonymous,
		emailVerified,
		metadata,
		color,
		role,
	}
}

/**
 * Returns the default state for the auth Vuex module.
 * Keeping it as a factory prevents shared mutated state across instances.
 */
const getDefaultState = () => {
	return {
		user: null,
	}
}

export default {
	state: getDefaultState(),
	getters: {
		isAuthenticated(state: { user: IUserAuth }) {
			return !!state.user
		},
		isAdmin(state: { user: IUserAuth }) {
			return !!state.user && state.user.role === "admin"
		},
		isFacilitator(state: { user: IUserAuth }) {
			return !!state.user && state.user.role === "facilitator"
		},
		user(state: { user: IUserAuth }) {
			return state.user
		},
	},
	mutations: {
		signIn(state: { user: IUserAuth }, user: IUserAuth) {
			state.user = { ...user }
		},
		signInAnonymously(state: { user: IUserAuth }, user: IUserAuth) {
			state.user = { ...user }
		},
		signOut(state: { user: IUserAuth | null }) {
			state.user = null
		},
		userRole(state: { user: IUserAuth }, user: IUserAuth) {
			state.user = { ...user }
		},
		updateUser(state: { user: IUserAuth }, user: IUserAuth) {
			state.user = { ...state.user, ...user }
		},
	},
	actions: {
		async signInWithEmailLink(
			{ commit }: { commit: Commit },
			data: { email: string; url: string }
		) {
			const auth = await getAuth()
			const userCredential: UserCredential = await signInWithEmailLink(
				auth,
				data.email,
				data.url
			)
			await update(ref(database, `users/${userCredential.user.uid}`), {
				metadata: { lastLoginAt: serverTimestamp() },
			})
			commit("signIn", mapToIUserAuth(userCredential.user as unknown as Partial<IUserAuth>))
		},
		async signInWithEmailAndPassword(
			{ commit }: { commit: Commit },
			{ email, password }: { email: string; password: string }
		) {
			try {
				const auth = await getAuth()
				const userCredential: UserCredential = await signInWithEmailAndPassword(
					auth,
					email,
					password
				)
				await update(ref(database, `users/${userCredential.user.uid}`), {
					metadata: { lastLoginAt: serverTimestamp() },
				})
				commit("signIn", mapToIUserAuth(userCredential.user as unknown as Partial<IUserAuth>))
			} catch (error) {
				onError(error as IError)
			}
		},
		async signInAnonymously({ commit }: { commit: Commit }) {
			try {
				const auth = await getAuth()
				const { user } = await signInAnonymously(auth)
				const appUser = mapToIUserAuth(user as unknown as Partial<IUserAuth>)
				commit("signInAnonymously", appUser)
				return appUser
			} catch (error) {
				onError(error as IError)
			}
		},
		async signOut({ commit }: { commit: Commit }) {
			try {
				const auth = await getAuth()
				await signOut(auth)
				commit("signOut")
			} catch (error) {
				onError(error as IError)
			}
		},
		async userRole(
				{ commit, getters }: { commit: Commit; getters: IAuthGetters },
			claims: IClaims
		) {
			const roleAdmin = claims.admin && "admin"
			const roleFacilitator = claims.facilitator && "facilitator"
			const role = roleAdmin || roleFacilitator || null
	
			if (getters.user && role) {
				const user = { ...getters.user, role }
				try {
					await update(ref(database, `users/${user.uid}`), {
						email: user.email,
						role: user.role,
						uid: user.uid,
						emailVerified: user.emailVerified,
						disabled: (user as Partial<IUserAuth>).disabled ?? false,
						tokensValidAfterTime: (user as Partial<IUserAuth>).tokensValidAfterTime ?? null,
					})
				} catch (error) {
					onError(error as IError)
				} finally {
					commit("userRole", user)
				}
			}
		},
		updateUser({ commit }: { commit: Commit }, user: IUserAuth) {
			commit("updateUser", user)
		},
		async resolveUserRole({ dispatch }: { dispatch: Dispatch }) {
			try {
				const auth = await getAuth()
				if (!auth) return

				// If currentUser is already present, use it
				const current = auth.currentUser as User | null
				if (current) {
					const { claims } = await current.getIdTokenResult(true)
					await dispatch("userRole", claims)
					return
				}

				// Otherwise wait for the next auth state change (one-shot)
				await new Promise<void>((resolve) => {
					const unsub = onAuthStateChanged(auth, async (u) => {
						try {
							if (u) {
								const { claims } = await u.getIdTokenResult(true)
								await dispatch("userRole", claims)
							}
						} catch (e) {
							onError(e as IError)
						} finally {
							unsub()
							resolve()
						}
					})
				})
			} catch (error) {
				onError(error as IError)
			}
		},
	},
}
